"use client";

import { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext({
  theme: 'light',
  setTheme: () => null,
});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export function ThemeProvider({ children, defaultTheme = 'system', storageKey = 'prep5-theme' }) {
  // Use getter function for useState to ensure proper initialization
  const [theme, setThemeState] = useState(() => {
    // Don't access localStorage during SSR
    if (typeof window === 'undefined') {
      return defaultTheme;
    }
    
    try {
      const storedTheme = localStorage.getItem(storageKey);
      return storedTheme || defaultTheme;
    } catch (error) {
      console.error('Error reading theme from localStorage:', error);
      return defaultTheme;
    }
  });

  // Sync with localStorage on mount and listen for cross-tab changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Initial sync on mount
    try {
      const storedTheme = localStorage.getItem(storageKey);
      if (storedTheme) {
        setThemeState(storedTheme);
      }
    } catch (error) {
      console.error('Error syncing theme from localStorage:', error);
    }
    
    // Listen for storage changes from other tabs
    const handleStorageChange = (e) => {
      if (e.key === storageKey && e.newValue) {
        setThemeState(e.newValue);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [storageKey]); // Removed 'theme' from dependencies

  useEffect(() => {
    if (typeof window === 'undefined' || !theme) return;
    
    const root = window.document.documentElement;
    
    // Remove previous theme classes
    root.classList.remove('light', 'dark');
    
    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      root.classList.add(systemTheme);
    } else {
      root.classList.add(theme);
    }
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = () => {
      if (theme === 'system') {
        const root = window.document.documentElement;
        root.classList.remove('light', 'dark');
        root.classList.add(mediaQuery.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  const value = {
    theme: theme || defaultTheme,
    setTheme: (newTheme) => {
      if (!newTheme) return;
      
      try {
        if (typeof window !== 'undefined') {
          localStorage.setItem(storageKey, newTheme);
        }
        setThemeState(newTheme);
      } catch (error) {
        console.error('Error setting theme:', error);
        // Still update state even if localStorage fails
        setThemeState(newTheme);
      }
    },
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
} 